/**
 * Secure session management utilities
 * Prevents sensitive data from being stored in insecure locations
 */

import { supabase } from '@xanectech/supabase-client';
import { createLogger, getEnvironmentConfig } from '@xanectech/lib';
import type { Json } from '@xanectech/supabase-client';
import {
  clearSensitiveLocalStorage,
  performLogoutCleanup,
} from './storageCleanup';

const logger = createLogger('Auth - secure-session');

const toJsonRecord = (value: Record<string, unknown> | undefined): Json => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  const normalized: Record<string, Json> = {};
  for (const [key, entryValue] of Object.entries(value)) {
    if (
      entryValue === null ||
      typeof entryValue === 'string' ||
      typeof entryValue === 'number' ||
      typeof entryValue === 'boolean'
    ) {
      normalized[key] = entryValue;
    }
  }

  return normalized;
};

export interface SecureSessionConfig {
  sessionTimeout: number; // in milliseconds
  maxInactivityTime: number; // in milliseconds
  enableAutoLogout: boolean;
}

class SecureSessionManager {
  public env: Record<string, unknown> = {};

  private config: SecureSessionConfig = {
    sessionTimeout: 24 * 60 * 60 * 1000, // 24 hours
    maxInactivityTime: 2 * 60 * 60 * 1000, // 2 hours
    enableAutoLogout: true,
  };

  private inactivityTimer: NodeJS.Timeout | null = null;
  private lastActivity: number = Date.now();

  /**
   * Initialize secure session management
   */
  initialize(config?: Partial<SecureSessionConfig>) {
    // logger.info('SecureSession: Initializing with config:', config);

    this.env = getEnvironmentConfig().loggingVars;

    if (config) {
      this.config = { ...this.config, ...config };
    }

    // logger.info('SecureSession: Final config:', this.config);

    if (this.config.enableAutoLogout) {
      // logger.info(
      //   'SecureSession: Auto-logout enabled, starting inactivity monitoring',
      // );
      this.startInactivityMonitoring();
    }
    // else {
    // logger.info('SecureSession: Auto-logout disabled');
    // }

    this.clearInsecureStorage();
  }

  /**
   * Clear potentially insecure storage locations
   */
  private clearInsecureStorage() {
    try {
      clearSensitiveLocalStorage();
    } catch (error) {
      logger.warn('Failed to clear insecure storage:', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  /**
   * Start monitoring user inactivity
   */
  private startInactivityMonitoring() {
    // logger.info('SecureSession: Starting inactivity monitoring with timeout:', {
    //   maxInactivityTimeMinutes: this.config.maxInactivityTime / 1000 / 60,
    // });

    // Track user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
    ];

    const handleActivity = () => {
      this.updateLastActivity();
    };

    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    // Start the inactivity timer
    this.resetInactivityTimer();
  }

  /**
   * Update last activity timestamp
   */
  private updateLastActivity() {
    const now = Date.now();
    this.lastActivity = now;
    // logger.info('SecureSession: Activity detected, resetting inactivity timer');
    this.resetInactivityTimer();
  }

  /**
   * Reset the inactivity timer
   */
  private resetInactivityTimer() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }

    this.inactivityTimer = setTimeout(() => {
      void this.handleInactivityTimeout();
    }, this.config.maxInactivityTime);
  }

  /**
   * Handle inactivity timeout
   */
  async handleInactivityTimeout() {
    // const inactiveTime = Date.now() - this.lastActivity;
    // logger.info('SecureSession: Inactivity timeout triggered.', {
    //   inactiveTime,
    //   maxInactivityTime: this.config.maxInactivityTime,
    // });

    try {
      await supabase.auth.signOut();

      // Clear all sensitive data
      this.clearInsecureStorage();

      // Redirect to login page
      window.location.href = '/auth';
    } catch (error) {
      logger.error('SecureSession: Failed to handle inactivity timeout.', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  /**
   * Check if session is active
   */
  isActive(): boolean {
    return this.inactivityTimer !== null;
  }

  /**
   * Get debug information about current state
   */
  getDebugInfo() {
    return {
      inactivityTimerActive: this.inactivityTimer !== null,
      lastActivity: this.lastActivity,
      minutesSinceLastActivity: Math.floor(
        (Date.now() - this.lastActivity) / 1000 / 60,
      ),
      config: this.config,
    };
  }

  /**
   * Securely end session
   */
  async endSession() {
    try {
      // Get current user before signing out
      const {
        data: { user },
      } = await supabase.auth.getUser();

      await supabase.auth.signOut();

      // Comprehensive cleanup
      await performLogoutCleanup({
        clearAllLocalStorage: false,
        clearEncryptionKeys: true,
        userId: user?.id,
      });

      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer);
        this.inactivityTimer = null;
      }
    } catch (error) {
      logger.error('SecureSession: Failed to end session securely.', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  /**
   * Log security event
   */
  async logSecurityEvent(event: string, details?: Record<string, unknown>) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user) {
        // Insert directly into audit_logs table instead of using RPC
        await supabase.from('audit_logs').insert({
          user_id: user.id,
          action: event,
          resource_type: 'session',
          details: toJsonRecord(details),
        });
      }
    } catch (error) {
      logger.warn('Failed to log security event:', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }
}

// Export singleton instance
export const secureSession = new SecureSessionManager();

// Add debug methods to global scope in development
if (typeof window !== 'undefined' && secureSession.env.VITE_DEV) {
  const debugWindow = window as Window & {
    debugSecureSession?: {
      getInfo: () => unknown;
      forceLogout: () => Promise<void>;
    };
  };
  debugWindow.debugSecureSession = {
    getInfo: () => secureSession.getDebugInfo(),
    forceLogout: () => secureSession.handleInactivityTimeout(),
  };
}
